import { AppThunk } from '../types';
import apis from '../../utils/apis';
import { handleApiError } from './utils/utils';

export interface AuditLogEntry {
  id: number;
  entity_type: string;
  entity_uuid: string;
  action: string;
  user_name: string | null;
  details: string | null;
  details_json: Record<string, any> | null;
  before_json: Record<string, any> | null;
  after_json: Record<string, any> | null;
  created_at: string;
  // Entity creator metadata (best-effort, may be absent)
  entity_created_by_name?: string;
  entity_created_by_role?: string;
}

export interface AuditLogPaginatedResponse {
  data: AuditLogEntry[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

export interface AuditLogQueryParams {
  entity_type?: string;
  action?: string;
  user_uuid?: string;
  /**
   * Everything recorded ABOUT this person, rather than everything they did.
   *
   * Accepts a uuid or a legacy numeric id. The endpoint enforces admin-or-self
   * and 403s otherwise, so callers that use it want `rethrow` (below) — a
   * refusal has to reach the pane, not be swallowed into an empty list that
   * reads as "this person has done nothing".
   */
  target_user_id?: string | number;
  user_only?: boolean;
  date_from?: string;
  date_to?: string;
  search?: string;
  page?: number;
  per_page?: number;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
}

const EMPTY_RESPONSE: AuditLogPaginatedResponse = {
  data: [],
  total: 0,
  page: 1,
  per_page: 50,
  total_pages: 0,
};

/** How a caller wants a failed query handled. */
export interface AuditLogQueryOptions {
  /**
   * Throw the error instead of turning it into a global toast and an empty
   * page.
   *
   * The list pages want the default: a transient failure there is noise, and
   * an empty table is an honest enough answer. A pane that has to tell the
   * difference between "no activity" and "you are not allowed to see this
   * activity" (403) cannot, unless the error survives the thunk.
   */
  rethrow?: boolean;
}

/**
 * Query the unified audit log with filters, pagination, and sorting.
 */
export const queryAuditLogs = (
  params: AuditLogQueryParams = {},
  options: AuditLogQueryOptions = {}
): AppThunk<Promise<AuditLogPaginatedResponse>> => async (dispatch) => {
  try {
    const response = await apis.get('/audit-logs', { params });
    if (response?.data?.success) {
      return {
        data: response.data.data || [],
        total: response.data.total || 0,
        page: response.data.page || 1,
        per_page: response.data.per_page || 50,
        total_pages: response.data.total_pages || 0,
      };
    }
    return EMPTY_RESPONSE;
  } catch (error: any) {
    if (options.rethrow) throw error;
    handleApiError(error, dispatch);
    return EMPTY_RESPONSE;
  }
};

/**
 * Get all audit log entries for a specific entity (side panel use).
 */
export const fetchEntityAuditLog = (
  entityType: string,
  entityUuid: string,
  page: number = 1,
  perPage: number = 50
): AppThunk<Promise<AuditLogPaginatedResponse>> => async (dispatch) => {
  try {
    const response = await apis.get(`/audit-logs/entity/${encodeURIComponent(entityType)}/${encodeURIComponent(entityUuid)}`, {
      params: { page, per_page: perPage },
    });
    if (response?.data?.success) {
      return {
        data: response.data.data || [],
        total: response.data.total || 0,
        page: response.data.page || 1,
        per_page: response.data.per_page || 50,
        total_pages: response.data.total_pages || 0,
      };
    }
    return EMPTY_RESPONSE;
  } catch (error: any) {
    handleApiError(error, dispatch);
    return EMPTY_RESPONSE;
  }
};
